/**
 * One person's presence on one workflow chart (AMO-178), over a transport:
 * the peers it hears of, and this person's own state, sent at most every
 * {@link PRESENCE_SEND_INTERVAL_MS} (the last of a burst always). When the
 * connection drops everyone else goes (their state is stale); the next
 * `sync` brings them back and this person's state is sent again. A chart
 * that is not theirs to see (not found) shares nothing; any other failure
 * tries again a little later. The room's word on the workflow itself (each
 * `sync`, every saved `revision`, eve's requests as they change) passes through
 * to its workflow listeners as it came. No IO of its own:
 * `./workflowPresence.ts` gives it the WebSocket.
 *
 * @module state/workflowPresenceChannel
 */
import type {
  WorkflowPeer,
  WorkflowPresencePushInput,
  WorkflowPresenceRef,
  WorkflowPresenceState,
  WorkflowPresenceStreamEvent,
} from "@amoreg/contracts";

/** At most this often a person's own presence is sent (~20 a second while something moves). */
export const PRESENCE_SEND_INTERVAL_MS = 50;
/** How long a failed subscription waits before trying again. */
export const PRESENCE_RETRY_MS = 5_000;

export const IDLE_PRESENCE: WorkflowPresenceState = {
  cursor: null,
  selection: null,
  drag: null,
  link: null,
  adding: null,
  editing: null,
};

export type PresenceWorkflowEvent =
  | Extract<WorkflowPresenceStreamEvent, { readonly type: "sync" | "revision" | "requests" }>
  /** The subscription dropped: nothing more is heard until the next `sync`. */
  | { readonly type: "lost" };

export interface PresenceSubscriptionHandlers {
  readonly onEvent: (event: WorkflowPresenceStreamEvent) => void;
  /** The subscription failed for good; it delivers nothing more. */
  readonly onError: (error: unknown) => void;
  /** The connection went down (false) or came back (true). */
  readonly onConnection: (online: boolean) => void;
}

export interface WorkflowPresenceTransport {
  /** Start the subscription; the returned function ends it. */
  readonly subscribe: (
    ref: WorkflowPresenceRef,
    handlers: PresenceSubscriptionHandlers,
  ) => () => void;
  readonly push: (input: WorkflowPresencePushInput) => Promise<void>;
}

export interface PresenceTimers {
  readonly now: () => number;
  readonly setTimeout: (run: () => void, ms: number) => unknown;
  readonly clearTimeout: (handle: unknown) => void;
}

const BROWSER_TIMERS: PresenceTimers = {
  now: () => Date.now(),
  setTimeout: (run, ms) => window.setTimeout(run, ms),
  clearTimeout: (handle) => window.clearTimeout(handle as number),
};

export interface PresenceChannel {
  /** Everyone else here, in the order they came. */
  readonly peers: () => ReadonlyArray<WorkflowPeer>;
  /** Called whenever `peers` changes. */
  readonly onPeers: (listener: () => void) => () => void;
  readonly onWorkflowEvent: (listener: (event: PresenceWorkflowEvent) => void) => () => void;
  /** Change this person's own presence; sent soon. */
  readonly publish: (patch: Partial<WorkflowPresenceState>) => void;
  readonly destroy: () => void;
}

/** One person's presence on one chart, over `transport`. */
export function openPresenceChannel(
  transport: WorkflowPresenceTransport,
  ref: WorkflowPresenceRef,
  timers: PresenceTimers = BROWSER_TIMERS,
): PresenceChannel {
  let state = IDLE_PRESENCE;
  let connection: string | null = null;
  let peers = new Map<string, WorkflowPeer>();
  let snapshot: ReadonlyArray<WorkflowPeer> = [];
  let lastSent = -Infinity;
  let sendTimer: unknown = null;
  let retryTimer: unknown = null;
  let unsubscribe: (() => void) | null = null;
  let destroyed = false;
  const listeners = new Set<() => void>();
  const workflowListeners = new Set<(event: PresenceWorkflowEvent) => void>();

  const toWorkflowListeners = (event: PresenceWorkflowEvent) => {
    for (const listener of workflowListeners) listener(event);
  };

  const changed = () => {
    snapshot = [...peers.values()];
    for (const listener of listeners) listener();
  };

  const send = () => {
    sendTimer = null;
    if (connection === null || destroyed) return;
    lastSent = timers.now();
    // A lost push is replaced by the next; nothing to report.
    transport.push({ ...ref, connection, state }).catch(() => undefined);
  };

  const schedule = () => {
    if (sendTimer !== null || connection === null) return;
    const wait = lastSent + PRESENCE_SEND_INTERVAL_MS - timers.now();
    if (wait <= 0) send();
    else sendTimer = timers.setTimeout(send, wait);
  };

  const forget = () => {
    if (connection !== null) toWorkflowListeners({ type: "lost" });
    connection = null;
    if (peers.size === 0) return;
    peers = new Map();
    changed();
  };

  const onEvent = (event: WorkflowPresenceStreamEvent) => {
    switch (event.type) {
      case "sync":
        connection = event.connection;
        peers = new Map(event.peers.map((peer) => [peer.connection, peer]));
        changed();
        // Back after a drop, or behind: say again what this person is doing.
        if (state !== IDLE_PRESENCE) schedule();
        toWorkflowListeners(event);
        return;
      case "peer":
        peers.set(event.peer.connection, event.peer);
        changed();
        return;
      case "left":
        if (peers.delete(event.connection)) changed();
        return;
      case "closed":
        forget();
        return;
      case "revision":
      case "requests":
        toWorkflowListeners(event);
        return;
    }
  };

  const start = () => {
    retryTimer = null;
    if (destroyed) return;
    unsubscribe = transport.subscribe(ref, {
      onEvent,
      onConnection: (online) => {
        if (!online) forget();
      },
      onError: (error) => {
        forget();
        unsubscribe?.();
        unsubscribe = null;
        // Not theirs to see: nothing to retry. Anything else (a cap, an outage): later.
        if (
          destroyed ||
          (error as { readonly _tag?: unknown } | null)?._tag === "WorkflowPresenceNotFoundError"
        )
          return;
        retryTimer = timers.setTimeout(start, PRESENCE_RETRY_MS);
      },
    });
  };
  start();

  return {
    peers: () => snapshot,
    onPeers: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    onWorkflowEvent: (listener) => {
      workflowListeners.add(listener);
      return () => workflowListeners.delete(listener);
    },
    publish: (patch) => {
      const next = { ...state, ...patch };
      if (sameState(next, state)) return;
      state = next;
      schedule();
    },
    destroy: () => {
      destroyed = true;
      if (sendTimer !== null) timers.clearTimeout(sendTimer);
      if (retryTimer !== null) timers.clearTimeout(retryTimer);
      unsubscribe?.();
      unsubscribe = null;
      listeners.clear();
      workflowListeners.clear();
    },
  };
}

const sameState = (a: WorkflowPresenceState, b: WorkflowPresenceState) =>
  a.cursor?.x === b.cursor?.x &&
  a.cursor?.y === b.cursor?.y &&
  a.selection === b.selection &&
  JSON.stringify(a.drag) === JSON.stringify(b.drag) &&
  JSON.stringify(a.link) === JSON.stringify(b.link) &&
  JSON.stringify(a.adding) === JSON.stringify(b.adding) &&
  JSON.stringify(a.editing ?? null) === JSON.stringify(b.editing ?? null);
